'use no memo'

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Dialog, DialogContent, Box, Card, CardContent, Typography, Divider, DialogActions, Grid } from '@mui/material'
import { useNotification } from '@/hooks/useNotification'
import { getMappingEmployeeSchema, UserTypeEnum, useMappingPhoneNumberMutation } from '@/@data'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslator } from '@/hooks/useTranslator'
import { Button } from '@/@core/components/mui/Button'
import CompanySelection from '../../selection/CompanySelection'
import UserSelection from '../../selection/UserSelection'
import { FormInput } from '../../form'
// import {  IUser, } from '@/@data'

interface EmployeeMappingDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  employee: any
  refetch?: () => void
}

export const EmployeeMappingDialog = ({ open, onClose, employee, onSuccess, refetch }: EmployeeMappingDialogProps) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const mappingPhoneNumberMutation = useMappingPhoneNumberMutation()
  const navigate = useNavigate()

  if (!employee) return null

  const methods = useForm<any>({
    defaultValues: {
      from: employee?.id || 0,
      to: 0,
      company_id: 0,
      system_code_n: '',
      employee_id_n: '',
      phone_n: '',
      name_n: '',
      company_name_n: ''
    },
    resolver: yupResolver(getMappingEmployeeSchema(__)),
    mode: 'onChange'
  })

  // Keep form values in sync if props change
  useEffect(() => {
    methods.reset({
      from: employee?.id || 0,
      to: 0,
      company_id: 0,
      business_number: employee?.company?.business_number || '',
      system_code: employee?.system_code || '',
      employee_id: employee?.employee_id || '',
      phone: employee?.phone || '',
      // Reset target mapping fields
      system_code_n: '',
      employee_id_n: '',
      phone_n: '',
      name_n: '',
      company_name_n: ''
    })
  }, [employee, methods])

  const handleSubmit = async (data: any) => {
    if (!employee) return

    try {
      const currentEmployeeCode = (data?.employee_id ?? '').toString()
      const targetEmployeeCode = (data?.employee_id_n ?? '').toString()

      if (currentEmployeeCode !== targetEmployeeCode) {
        n.error(__('Your Employee Code does not match'))

        return
      }

      const payload = {
        ...data,
        employee_from_id: data.employee_id_n
      }

      delete payload.business_number
      delete payload.system_code_n
      delete payload.employee_id_n
      delete payload.phone_n
      delete payload.name_n
      delete payload.company_name_n
      delete payload.phone
      delete payload.system_code

      await mappingPhoneNumberMutation.mutateAsync(payload)
      n.success(__('Mapping employee successfully'))
      onSuccess?.()
      if (refetch) refetch()

      const toId = methods.getValues('to')

      onClose()

      if (toId) {
        navigate(`/cms/users/employee/${toId}`)
      }
    } catch (error) {
      n.error(__('Failed to map employee'))
    }
  }

  const handleCloseAndNavigate = () => {
    onClose()
    methods.reset()
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseAndNavigate}
      maxWidth='md'
      scroll='body'
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '930', borderRadius: '32px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent className='p-0 m-0' sx={{ padding: 0, borderRadius: '32px' }}>
            <DialogCloseButton onClick={handleCloseAndNavigate} disableRipple>
              <i className='tabler-x' />
            </DialogCloseButton>
            <Card>
              <CardContent>
                <Typography variant='h5' className='text-[#2F2B3DE5] mb-6'>
                  {__('Mapping Employee ')}
                </Typography>

                <Box className='flex items-center justify-center gap-6'>
                  {/* From card */}
                  <Box className='flex-1 rounded-2xl bg-[#3551F214] p-6 border border-[#E6E8F0]'>
                    <Typography variant='h6' className='text-[#2F2B3DE5]'>
                      {__('Mapping Employee From')}
                    </Typography>
                    <Divider className='!my-4' />
                    <Box className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Name')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.first_name || employee?.last_name
                            ? `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()
                            : '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employer')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.company?.name || employee?.company_name || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Code')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.employee_id || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Phone')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.phone_code} {employee?.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Link icon */}
                  <Box className='hidden md:flex items-center justify-center'>
                    <i className='tabler-link text-[28px] text-[#4B5BFF]' />
                  </Box>

                  {/* To card */}
                  <Box className='flex-1 rounded-2xl bg-[#3551F214] p-6 border border-[#E6E8F0]'>
                    <Typography variant='h6' className='text-[#2F2B3DE5]'>
                      {__('Mapping Employee To')}
                    </Typography>
                    <Divider className='!my-4' />
                    <Box className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Name')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {methods.watch('name_n') || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employer')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {methods.watch('company_name_n') || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Code')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {methods.watch('employee_id_n') || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Phone')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.phone_code} {methods.watch('phone_n') || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={3} className='mt-6'>
                  <Grid size={4}>
                    <Controller
                      name='company_id'
                      control={methods.control}
                      render={({ field }) => (
                        <CompanySelection
                          {...field}
                          fullWidth
                          required
                          label={__('Employer')}
                          onClear={() => {
                            methods.setValue('company_id', 0)
                            methods.setValue('business_number', '')
                            methods.setValue('company_name_n', '')
                          }}
                          onChange={(value, selected) => {
                            field.onChange(value)

                            if (selected) {
                              methods.setValue('business_number', selected.business_number)
                              methods.setValue('company_name_n', selected.name || '')
                            }

                            if (!selected) {
                              methods.setValue('business_number', '')
                              methods.setValue('company_name_n', '')
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={4}>
                    <Controller
                      name='to'
                      control={methods.control}
                      render={({ field }) => (
                        <UserSelection
                          {...field}
                          fullWidth
                          required
                          label={__('Employee')}
                          user_type={UserTypeEnum.User}
                          company_id={methods.watch('company_id') || undefined}
                          onClear={() => {
                            methods.setValue('to', 0)
                            methods.setValue('system_code_n', '')
                            methods.setValue('employee_id_n', '')
                            methods.setValue('phone_n', '')
                            methods.setValue('name_n', '')
                            methods.setValue('company_id', methods.watch('company_id') || 0)
                          }}
                          onChange={(value, selected) => {
                            field.onChange(value)

                            if (selected) {
                              methods.setValue('system_code_n', selected.system_code)
                              methods.setValue('employee_id_n', selected.employee_id)
                              methods.setValue('phone_n', selected.phone)
                              const fullName = `${selected.first_name || ''} ${selected.last_name || ''}`.trim()

                              methods.setValue('name_n', fullName)
                            }

                            if (!selected) {
                              methods.setValue('system_code_n', '')
                              methods.setValue('employee_id_n', '')
                              methods.setValue('phone_n', '')
                              methods.setValue('name_n', '')
                            }
                          }}
                          disabled={!methods.watch('company_id') || false}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={4}>
                    <FormInput
                      name='business_number'
                      placeholder={__('Employer Registration')}
                      control={methods.control}
                      label={__('Employer Registration')}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid size={4}>
                    <FormInput
                      name='system_code_n'
                      placeholder={__('System Code')}
                      control={methods.control}
                      label={__('System Code')}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid size={4}>
                    <FormInput
                      name='employee_id_n'
                      placeholder={__('Employee Code')}
                      control={methods.control}
                      label={__('Employee Code')}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid size={4}>
                    <FormInput
                      name='phone_n'
                      placeholder={__('Phone')}
                      control={methods.control}
                      label={__('Phone')}
                      fullWidth
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </DialogContent>

          <DialogActions className='px-[20px] sm:px-[24px] text-center justify-end gap-2'>
            <Button variant='outlined' onClick={handleCloseAndNavigate} startIcon={<i className='tabler-arrow-left' />}>
              {__('Previous')}
            </Button>
            <Button variant='contained' type='submit' startIcon={<i className='tabler-link' />} color='success'>
              {__('Confirm Mapping Employee')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
